// Email Monitor Decisions API Service

import api from './api';

export interface FeedbackInfo {
  user_feedback: 'thumbs_up' | 'thumbs_down' | null;
  feedback_text: string | null;
  feedback_at: string | null;
  knowledge_updated: boolean;
}

export interface OrderLineItem {
  item_code: string;
  quantity_ordered: number;
}

export interface OrderInfo {
  customer_no?: string;
  customer_po_no?: string | null;
  udf_required_by?: string | null;
  line_items?: OrderLineItem[];
}

export interface EmailMonitorDecision {
  id: string;
  notification_id: string | null;
  email_id: string;
  classification_id: string;
  salesperson_no: string | null;
  email_context: Record<string, unknown>;
  agent_messages: Array<Record<string, unknown>>;
  decision: 'notify' | 'unsure' | 'ignore';
  confidence: number;
  notification_type: string;
  title: string;
  message: string;
  reasoning: string;
  model_name: string;
  total_tokens: number;
  total_cost_usd: number;
  agent_iterations: number;
  latency_ms: number;
  feedback_processed: boolean;
  expires_at: string;
  created_at: string;
  feedback?: FeedbackInfo | null;
  order_info?: OrderInfo | null;
  category?: string | null;
}

export interface DecisionListResponse {
  decisions: EmailMonitorDecision[];
  total: number;
  limit: number;
  offset: number;
}

export interface DecisionStatsResponse {
  total_decisions: number;
  notify_count: number;
  unsure_count: number;
  ignore_count: number;
  avg_confidence: number | null;
  avg_latency_ms: number | null;
  total_tokens: number;
  total_cost_usd: number;
  feedback_processed_count: number;
}

export type SortField =
  | 'created_at'
  | 'decision'
  | 'confidence'
  | 'notification_type'
  | 'total_tokens'
  | 'total_cost_usd'
  | 'latency_ms'
  | 'salesperson_no';

export type SortOrder = 'asc' | 'desc';

export interface ListDecisionsParams {
  decision?: string;
  notification_type?: string;
  salesperson_no?: string;
  email_id?: string;
  search?: string;
  sort_by?: SortField;
  sort_order?: SortOrder;
  limit?: number;
  offset?: number;
}

// Pipeline control types
export interface PipelineStatus {
  scheduler_enabled: boolean;
  queue_size: number;
  queue_capacity: number;
  never_processed_count: number;
  error_count: number;
  processing_count: number;
  dead_letter_count: number;
}

export interface DeadLetterEmail {
  id: string;
  provider_message_id: string;
  subject: string | null;
  sender: string | null;
  classification_error: string | null;
  classification_retry_count: number;
  synced_at: string | null;
}

export interface DeadLetterQueueResponse {
  emails: DeadLetterEmail[];
  total: number;
  max_retries: number;
}

export interface RetryDeadLetterResponse {
  success: boolean;
  message: string;
}

export interface RunBatchRequest {
  batch_size?: number;
  model?: string;
}

export interface RunBatchResponse {
  queued: number;
  skipped: number;
  queue_size_before: number;
  queue_capacity: number;
  message: string;
}

export interface ToggleSchedulerResponse {
  scheduler_enabled: boolean;
  message: string;
}

export interface ResetPipelineResponse {
  decisions_deleted: number;
  classifications_deleted: number;
  emails_reset: number;
  message: string;
}

export interface ErrorEmail {
  id: string;
  provider_message_id: string;
  subject: string | null;
  sender: string | null;
  classification_error: string | null;
  classification_retry_count: number;
  synced_at: string | null;
  is_dead_letter: boolean;
}

export interface ErrorEmailsResponse {
  emails: ErrorEmail[];
  total: number;
  max_retries: number;
}

// Routing rules types
export interface RoutingRuleItem {
  notification_type: string;
  category: string;
  routing_target: string;
  description: string;
}

export interface RoutingRulesResponse {
  rules: RoutingRuleItem[];
  decision_outcomes: Array<{ outcome: string; description: string }>;
  agent_guidelines: string[];
}

// =============================================================================
// Classification types (merged from emailClassification service)
// =============================================================================

export type EmailCategory =
  | 'order'
  | 'quote'
  | 'inquiry'
  | 'support'
  | 'payment'
  | 'shipping'
  | 'internal'
  | 'spam'
  | 'other';

export interface EmailClassificationResult {
  id: string;
  synced_email_id: string;
  // LLM classification
  llm_category: EmailCategory;
  llm_confidence: number;
  // Deterministic extraction-confidence bucket. Operational-only signal,
  // computed at agent completion. NULL on rows written before rollout.
  confidence_bucket: 'high' | 'medium' | 'low' | null;
  llm_sub_categories: string[] | null;
  llm_summary: string | null;
  llm_classification_reason: string | null;
  llm_order_info: Record<string, unknown> | null;
  llm_is_from_customer: boolean;
  llm_requires_response: boolean;
  llm_is_urgent: boolean;
  // LLM metadata
  model_used: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  // Sender match fields
  sender_matched: boolean;
  sender_match_method: string | null;
  sender_match_reason: string | null;
  matched_customer_id: string | null;
  matched_customer_name: string | null;
  matched_customer_no: string | null;
  matched_salesperson_id: string | null;
  matched_salesperson_name: string | null;
  matched_salesperson_no: string | null;
  // CSR assignment
  csr_name: string | null;
  csr_assignment_type: 'suggested' | null;
  csr_confidence: number | null;
  csr_method: string | null;
  csr_evidence: string | null;
  // Human verification
  human_category: EmailCategory | null;
  human_verified: boolean;
  human_verified_by: string | null;
  human_verified_at: string | null;
  human_notes: string | null;
  // Computed
  effective_category: EmailCategory;
  // Timestamps
  created_at: string;
  updated_at: string;
  // Email preview data
  email_subject: string | null;
  email_from: string | null;
  email_received_at: string | null;
  // Detail-only fields
  email_to?: string | null;
  email_cc?: string | null;
  email_body_text?: string | null;
  email_body_html?: string | null;
}

export interface ClassificationResultsResponse {
  items: EmailClassificationResult[];
  total: number;
  offset: number;
  limit: number;
}

export interface EmailClassificationStats {
  total_classified: number;
  total_unclassified: number;
  by_category: Record<string, number>;
  human_verified_count: number;
  accuracy: number | null;
  accuracy_by_category: Record<string, number> | null;
  avg_confidence: number;
  low_confidence_count: number;
  high_confidence_count: number;
  processing_status: Record<string, number>;
}

export interface GetClassificationParams {
  category?: string;
  human_verified?: boolean;
  sender_matched?: boolean;
  min_confidence?: number;
  search?: string;
  sort_by?: 'created_at' | 'llm_confidence' | 'llm_category' | 'confidence' | 'category';
  sort_order?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
}

export const emailMonitorDecisionService = {
  listDecisions: async (
    params?: ListDecisionsParams,
  ): Promise<DecisionListResponse> => {
    const response = await api.get<DecisionListResponse>(
      '/admin/email-monitor-decisions',
      { params },
    );
    return response.data;
  },

  getStats: async (): Promise<DecisionStatsResponse> => {
    const response = await api.get<DecisionStatsResponse>(
      '/admin/email-monitor-decisions/stats',
    );
    return response.data;
  },

  getDecision: async (decisionId: string): Promise<EmailMonitorDecision> => {
    const response = await api.get<EmailMonitorDecision>(
      `/admin/email-monitor-decisions/${decisionId}`,
    );
    return response.data;
  },

  getNotificationTypes: async (): Promise<string[]> => {
    const response = await api.get<string[]>(
      '/admin/email-monitor-decisions/notification-types',
    );
    return response.data;
  },

  // Pipeline control
  getPipelineStatus: async (): Promise<PipelineStatus> => {
    const response = await api.get<PipelineStatus>(
      '/admin/email-monitor-decisions/pipeline-status',
    );
    return response.data;
  },

  runBatch: async (params?: RunBatchRequest): Promise<RunBatchResponse> => {
    const response = await api.post<RunBatchResponse>(
      '/admin/email-monitor-decisions/run-batch',
      params || {},
    );
    return response.data;
  },

  toggleScheduler: async (enabled: boolean): Promise<ToggleSchedulerResponse> => {
    const response = await api.post<ToggleSchedulerResponse>(
      '/admin/email-monitor-decisions/toggle-scheduler',
      { enabled },
    );
    return response.data;
  },

  // Routing rules (read-only)
  getRoutingRules: async (): Promise<RoutingRulesResponse> => {
    const response = await api.get<RoutingRulesResponse>(
      '/admin/email-monitor-decisions/routing-rules',
    );
    return response.data;
  },

  // Dead letter queue
  getDeadLetterQueue: async (
    limit = 50,
    offset = 0,
  ): Promise<DeadLetterQueueResponse> => {
    const response = await api.get<DeadLetterQueueResponse>(
      '/admin/email-monitor-decisions/dead-letter-queue',
      { params: { limit, offset } },
    );
    return response.data;
  },

  retryDeadLetterEmail: async (
    emailId: string,
  ): Promise<RetryDeadLetterResponse> => {
    const response = await api.post<RetryDeadLetterResponse>(
      '/admin/email-monitor-decisions/dead-letter-queue/retry',
      { email_id: emailId },
    );
    return response.data;
  },

  // Error emails (all errors, not just DLQ)
  getErrorEmails: async (
    limit = 50,
    offset = 0,
  ): Promise<ErrorEmailsResponse> => {
    const response = await api.get<ErrorEmailsResponse>(
      '/admin/email-monitor-decisions/error-emails',
      { params: { limit, offset } },
    );
    return response.data;
  },

  retryErrorEmail: async (
    emailId: string,
  ): Promise<RetryDeadLetterResponse> => {
    const response = await api.post<RetryDeadLetterResponse>(
      '/admin/email-monitor-decisions/error-emails/retry',
      { email_id: emailId },
    );
    return response.data;
  },

  // Pipeline reset
  resetPipeline: async (): Promise<ResetPipelineResponse> => {
    const response = await api.post<ResetPipelineResponse>(
      '/admin/email-monitor-decisions/reset-pipeline',
    );
    return response.data;
  },

  // ==========================================================================
  // Classification endpoints (calls existing /email-classification routes)
  // ==========================================================================

  getClassificationResults: async (
    params?: GetClassificationParams,
  ): Promise<ClassificationResultsResponse> => {
    const response = await api.get<ClassificationResultsResponse>(
      '/email-classification/results',
      { params },
    );
    return response.data;
  },

  getClassificationResult: async (
    classificationId: string,
  ): Promise<EmailClassificationResult> => {
    const response = await api.get<EmailClassificationResult>(
      `/email-classification/results/${classificationId}`,
    );
    return response.data;
  },

  getClassificationStats: async (): Promise<EmailClassificationStats> => {
    const response = await api.get<EmailClassificationStats>(
      '/email-classification/stats',
    );
    return response.data;
  },

  getCategories: async (): Promise<{ value: string; label: string }[]> => {
    const response = await api.get<{ value: string; label: string }[]>(
      '/email-classification/categories',
    );
    return response.data;
  },

  verifyClassification: async (
    classificationId: string,
    category?: string,
    notes?: string,
  ): Promise<EmailClassificationResult> => {
    const response = await api.post<EmailClassificationResult>(
      `/email-classification/results/${classificationId}/verify`,
      { category: category || null, notes: notes || null },
    );
    return response.data;
  },
};

export default emailMonitorDecisionService;
